import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../api/api.service';
import { GetNotUsingMyVehicle, NotUsingMyVehicleInsert } from 'src/app/models/not-using-my-vehicle.model';
import { DriverResponse } from 'src/app/models/driver.model';
import { SqliteStorageService } from '../sqlite-storage/sqlite-storage.service';
import { UserProvider } from '../providers/user-provider.ts/user-provider';

@Injectable({
  providedIn: 'root',
})
export class NotUsingMyVehicleService {
  private pendingDeclaration: Promise<void> | null = null;
  private declarationData: GetNotUsingMyVehicle | null = null;

  get readDeclarationState(): GetNotUsingMyVehicle | null {
    return this.declarationData;
  }

  constructor(
    private readonly apiService: ApiService,
    private readonly sqliteService: SqliteStorageService,
    private readonly userProvider: UserProvider
  ) { }

  insertDeclaration(data: NotUsingMyVehicleInsert) {
    this.declarationData = null;
    return this.apiService.post<NotUsingMyVehicleInsert, number>('NotUsingMyVehicle/InsertDeclaration', data);
  }

  async cacheDeclaration(data: Date): Promise<void> {
    await this.sqliteService.setItem('NotUsingMyVehicleStatus', data);
  }

  resetDeclarationState(): void {
    this.pendingDeclaration = null;
    this.declarationData = null;
  }

  async getDeclaration(): Promise<void> {
    if (!navigator.onLine || await this.hasNoAssignedVehicle()) {
      return;
    }

    if (this.pendingDeclaration) {
      return this.pendingDeclaration;
    }

    if (this.declarationData) {
      return;
    }

    this.pendingDeclaration = firstValueFrom(
      this.apiService.get<GetNotUsingMyVehicle>('NotUsingMyVehicle/GetNotUsingMyVehicle')
    ).then(async (result) => {
      this.declarationData = result;
      if (!await this.sqliteService.getItem<Date>('NotUsingMyVehicleStatus')) {
        await this.cacheDeclaration(result.lastDeclarationDate);
      }
    }).catch((error) => {
      throw error;
    }).finally(() => {
      this.pendingDeclaration = null;
    });

    return this.pendingDeclaration;
  }

  async getDeclarationFromSql(): Promise<GetNotUsingMyVehicle | null> {
    // The declaration never applies without a vehicle, and any earlier state must not trigger a prompt
    if (await this.hasNoAssignedVehicle()) {
      return null;
    }
    if (this.pendingDeclaration) {
      return this.declarationData ?? null;
    }

    if (!navigator.onLine) {
      return this.declarationData;
    }

    if (this.declarationData && this.declarationData.featureEnabled !== 1) {
      return this.declarationData;
    }

    const stored = await this.sqliteService.getItem<Date>(
      'NotUsingMyVehicleStatus'
    );

    if (!stored || !this.isToday(stored)) {
      await this.getDeclaration();
    }

    return this.declarationData;
  }

  // Unknown driver data is not treated as unassigned, so existing behaviour is kept until it is known
  private async hasNoAssignedVehicle(): Promise<boolean> {
    const driverData = this.userProvider.readDriverData()
      ?? await this.sqliteService.getItem<DriverResponse>('meData');

    return !!driverData && !driverData.vehicles?.length;
  }


  private isToday(date: any): boolean {
    date = new Date(date);
    const today = new Date();
    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    );
  }

  async shouldPromptDeclaration(declaration: GetNotUsingMyVehicle | null): Promise<boolean> {
    return declaration?.featureEnabled === 1 && declaration?.isVisitedToday === 0;
  }
}
